import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Gift, Sparkles, Users, Copy, Check, TrendingUp, ArrowUpRight, Crown, Zap, Wallet,
  Home, ClipboardList, Calendar, MessageCircle, Loader2, X, Lock, Award,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const LEDGER_LABEL = { earn: "Reward earned", referral: "Referral bonus", bonus: "Bonus", redeem: "Redeemed", adjustment: "Adjustment" };
const REWARD_ICONS = { wallet: Wallet, sparkles: Sparkles, zap: Zap, crown: Crown, gift: Gift };
const EARN_ICONS = { gift: Gift, home: Home, clipboard: ClipboardList, calendar: Calendar };
const EARN_LINK = { add_property: "/account/properties", complete_passport: "/account/properties", first_online_booking: "/property-intelligence" };

export default function AccountRewards() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [copied, setCopied] = useState(false);
  const [confirmReward, setConfirmReward] = useState(null);
  const [redeeming, setRedeeming] = useState(false);

  const load = useCallback(() => { api.get("/client/membership").then(({ data }) => setD(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const rewardsOn = d?.settings?.rewards_enabled;
  const referralsOn = d?.settings?.referrals_enabled;
  const m = d?.membership || {};
  const balance = m.points_balance ?? 0;
  const referral = d?.referral || {};
  const code = referral.code;
  const link = referral.link;

  const copy = () => { if (!code) return; navigator.clipboard?.writeText(link || code); setCopied(true); toast.success("Referral link copied"); setTimeout(() => setCopied(false), 1500); };
  const shareWhatsApp = () => {
    const text = `I've been using Ascendia for premium property detailing — join with my link and we both get rewarded: ${link || ""}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  const doRedeem = async () => {
    if (!confirmReward) return;
    setRedeeming(true);
    try {
      const { data } = await api.post("/client/rewards/redeem", { reward_id: confirmReward.id });
      toast.success(`Reward claimed · code ${data.redemption?.code}`);
      setConfirmReward(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not claim reward"); }
    finally { setRedeeming(false); }
  };

  if (!d) return <div className="py-20 flex justify-center text-muted-c"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const progress = d.progress || {};
  const tiers = d.tiers || [];
  const catalog = d.catalog || [];
  const opps = d.earn_opportunities || [];
  const redemptions = d.redemptions || [];

  return (
    <div className="space-y-6" data-testid="account-rewards">
      <div>
        <p className="kicker mb-1">My Ascendia</p>
        <h1 className="font-display text-2xl sm:text-3xl font-semibold text-white">Membership &amp; rewards</h1>
      </div>

      {/* Ascendia credit — auto-applied to next quote */}
      {(d.reward_credit || 0) > 0 && (
        <div className="ascendia-card p-4 flex items-center gap-3 border-[color:var(--border-strong)] bg-gold/[0.06]" data-testid="reward-credit-banner">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold/15"><Wallet className="h-5 w-5 text-gold-soft" /></span>
          <div>
            <p className="text-white font-semibold">₪{Number(d.reward_credit).toLocaleString()} Ascendia credit</p>
            <p className="text-xs text-muted-c">Automatically applied to your next quote — nothing to do.</p>
          </div>
        </div>
      )}

      {/* Membership hero + progress */}
      <div className="ascendia-card card-accent-top p-6" data-testid="rewards-hero">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1"><Crown className="h-5 w-5 text-gold-soft" /><span className="text-[11px] uppercase tracking-[0.16em] text-gold-soft">{m.membership_tier || "Standard"} member</span></div>
            <p className="font-display text-4xl font-semibold text-white" data-testid="points-balance">{balance.toLocaleString()}<span className="text-lg text-muted-c font-normal"> pts</span></p>
            <p className="text-xs text-muted-c mt-1 flex items-center gap-1.5"><TrendingUp className="h-3.5 w-3.5 text-gold-soft" /> {(m.lifetime_points ?? 0).toLocaleString()} lifetime · redeemable anytime below</p>
          </div>
          {progress.next && (
            <div className="text-right">
              <p className="text-xs text-muted-c">{progress.points_to_next?.toLocaleString()} pts to <span className="text-gold-soft">{progress.next}</span></p>
            </div>
          )}
        </div>
        {progress.next && (
          <div className="mt-4">
            <div className="h-2 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full rounded-full bg-gold transition-[width] duration-500" style={{ width: `${progress.pct || 0}%` }} data-testid="tier-progress-bar" />
            </div>
            <div className="mt-1.5 flex justify-between text-[11px] text-muted-c"><span>{progress.current}</span><span>{progress.next}</span></div>
          </div>
        )}
      </div>

      {/* Tier ladder */}
      <div className="ascendia-card p-6" data-testid="tier-ladder">
        <div className="flex items-center gap-2 mb-4"><Award className="h-5 w-5 text-gold-soft" /><h3 className="font-display font-semibold text-white">Membership tiers</h3></div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {tiers.map((t) => {
            const current = (m.membership_tier || "Standard") === t.name;
            return (
              <div key={t.name} className={`rounded-xl border p-4 ${current ? "border-gold bg-gold/[0.06]" : t.reached ? "border-[color:var(--border-subtle)]" : "border-[color:var(--border-subtle)]/50 opacity-70"}`} data-testid={`tier-${t.name.replace(/\s/g,'-')}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className={`text-sm font-semibold ${current ? "text-gold-soft" : "text-white"}`}>{t.name}</span>
                  {current ? <span className="text-[10px] uppercase tracking-wide text-gold-soft border border-[color:var(--border-strong)] rounded-full px-2 py-0.5">You</span>
                    : t.reached ? <Check className="h-4 w-4 text-emerald-300" /> : <Lock className="h-3.5 w-3.5 text-muted-c" />}
                </div>
                <p className="text-[11px] text-muted-c mb-2">{t.threshold.toLocaleString()} lifetime pts</p>
                <ul className="space-y-1">
                  {(t.perks || []).map((p, i) => <li key={i} className="text-xs text-secondary flex items-start gap-1.5"><Sparkles className="h-3 w-3 text-gold-soft mt-0.5 shrink-0" />{p}</li>)}
                </ul>
              </div>
            );
          })}
        </div>
      </div>

      {/* Ways to earn */}
      {rewardsOn && opps.length > 0 && (
        <div className="ascendia-card p-6" data-testid="ways-to-earn">
          <div className="flex items-center gap-2 mb-4"><Zap className="h-5 w-5 text-gold-soft" /><h3 className="font-display font-semibold text-white">Ways to earn</h3></div>
          <div className="grid gap-2.5 sm:grid-cols-2">
            {opps.map((o) => {
              const Icon = EARN_ICONS[o.icon] || Gift;
              const to = EARN_LINK[o.id];
              return (
                <button key={o.id} disabled={o.done || !to} onClick={() => to && nav(to)} data-testid={`earn-${o.id}`}
                  className={`flex items-center gap-3 rounded-xl border p-3.5 text-left transition-colors ${o.done ? "border-[color:var(--border-subtle)] bg-emerald-500/[0.05]" : to ? "border-[color:var(--border-subtle)] hover:bg-white/[0.03] cursor-pointer" : "border-[color:var(--border-subtle)]/60"}`}>
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${o.done ? "bg-emerald-500/15" : "bg-gold/10"}`}>
                    {o.done ? <Check className="h-4 w-4 text-emerald-300" /> : <Icon className="h-4 w-4 text-gold-soft" />}
                  </span>
                  <span className="flex-1">
                    <span className="block text-sm text-white">{o.title}</span>
                    <span className={`text-xs ${o.done ? "text-emerald-300" : "text-muted-c"}`}>{o.done ? "Earned" : `+${o.points} pts`}</span>
                  </span>
                  {!o.done && to && <ArrowUpRight className="h-4 w-4 text-muted-c" />}
                </button>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-muted-c">Plus <span className="text-secondary">{d.settings?.earn_points_per_currency || 1} pt per ₪1</span> on every completed service.</p>
        </div>
      )}

      {/* Redeem rewards */}
      {rewardsOn && catalog.length > 0 && (
        <div className="ascendia-card p-6" data-testid="redeem-catalog">
          <div className="flex items-center gap-2 mb-4"><Gift className="h-5 w-5 text-gold-soft" /><h3 className="font-display font-semibold text-white">Redeem your points</h3></div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {catalog.map((r) => {
              const Icon = REWARD_ICONS[r.icon] || Gift;
              const afford = balance >= r.points_cost;
              return (
                <div key={r.id} className="rounded-xl border border-[color:var(--border-subtle)] p-4 flex flex-col" data-testid={`reward-${r.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gold/10"><Icon className="h-4 w-4 text-gold-soft" /></span>
                    <span className="text-sm font-semibold text-gold-soft">{r.points_cost.toLocaleString()} pts</span>
                  </div>
                  <p className="text-sm font-medium text-white">{r.title}</p>
                  <p className="text-xs text-muted-c mt-1 flex-1">{r.description}</p>
                  <button onClick={() => setConfirmReward(r)} disabled={!afford} data-testid={`redeem-${r.id}`}
                    className={`mt-3 w-full rounded-lg py-2 text-xs font-semibold transition-colors ${afford ? "bg-gold text-black hover:bg-gold-soft" : "border border-[color:var(--border-subtle)] text-muted-c cursor-not-allowed"}`}>
                    {afford ? "Claim reward" : `Need ${(r.points_cost - balance).toLocaleString()} more`}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Your claimed rewards */}
      {redemptions.length > 0 && (
        <div className="ascendia-card p-6" data-testid="my-redemptions">
          <h3 className="font-display font-semibold text-white mb-4">Your rewards</h3>
          <ul className="space-y-2.5">
            {redemptions.map((r) => (
              <li key={r.id} className="flex items-center justify-between rounded-xl border border-[color:var(--border-subtle)] p-3">
                <div>
                  <p className="text-sm text-white">{r.title}</p>
                  <p className="text-xs text-muted-c">Show code <span className="font-mono text-gold-soft">{r.code}</span> · we'll apply it to your next service</p>
                </div>
                <span className={`text-[10px] uppercase tracking-wide rounded-full px-2 py-0.5 border ${r.status === "issued" ? "border-gold/40 text-gold-soft" : "border-emerald-500/40 text-emerald-300"}`}>{r.status === "issued" ? "Ready to use" : r.status}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Referral */}
      <div className="ascendia-card p-6" data-testid="referral-card">
        <div className="flex items-center gap-2 mb-3"><Users className="h-5 w-5 text-gold-soft" /><h3 className="font-display font-semibold text-white">Refer a friend, earn when they book</h3>
          {!referralsOn && <span className="ml-auto text-[10px] uppercase tracking-[0.16em] text-gold-soft border border-[color:var(--border-subtle)] rounded-full px-2 py-0.5">Coming soon</span>}
        </div>
        <p className="text-sm text-muted-c">
          You earn <span className="text-gold-soft">{(referral.bonus_points || 0).toLocaleString()} pts</span> when a friend you refer completes their first Ascendia service. Real rewards for real bookings.
        </p>
        {code && (
          <>
            <div className="mt-3 flex items-center gap-2">
              <code className="flex-1 truncate rounded-lg border border-[color:var(--border-subtle)] bg-black/40 px-3 py-2 text-sm text-gold-soft font-mono" data-testid="referral-code">{link || code}</code>
              <button onClick={copy} title="Copy link" className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-[color:var(--border-subtle)] text-white/70 hover:bg-white/5">{copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}</button>
            </div>
            <button onClick={shareWhatsApp} data-testid="referral-whatsapp" className="mt-2.5 inline-flex items-center gap-2 rounded-lg bg-[#25D366]/15 border border-[#25D366]/40 px-4 py-2 text-sm font-medium text-[#25D366] hover:bg-[#25D366]/25">
              <MessageCircle className="h-4 w-4" /> Share on WhatsApp
            </button>
          </>
        )}
        <p className="mt-2.5 text-xs text-muted-c">Successful referrals: {referral.count ?? 0}</p>
      </div>

      {/* Points history */}
      {rewardsOn && (
        <div className="ascendia-card p-6" data-testid="rewards-ledger">
          <h3 className="font-display font-semibold text-white mb-4">Points history</h3>
          {d.ledger?.length ? (
            <ul className="divide-y divide-[color:var(--border-subtle)]/40">
              {d.ledger.map((t) => (
                <li key={t.transaction_id} className="flex items-center justify-between py-2.5">
                  <div><p className="text-sm text-white">{LEDGER_LABEL[t.type] || t.type}</p><p className="text-xs text-muted-c">{t.reason} · {(t.created_at || "").slice(0, 10)}</p></div>
                  <span className={`inline-flex items-center gap-1 text-sm font-medium ${t.type === "redeem" || t.amount < 0 ? "text-red-300" : "text-emerald-300"}`}>{(t.type === "redeem" || t.amount < 0) ? "−" : "+"}{Math.abs(Number(t.amount)).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-muted-c">No points yet. Complete an action above or your first service to start earning.</p>}
        </div>
      )}

      {/* Redeem confirm modal */}
      {confirmReward && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setConfirmReward(null)} data-testid="redeem-confirm">
          <div className="w-full max-w-sm rounded-2xl bg-[#101011] border border-[color:var(--border-subtle)] p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-1">
              <h3 className="font-display text-lg font-semibold text-white">Claim this reward?</h3>
              <button onClick={() => setConfirmReward(null)} className="text-muted-c hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <p className="text-sm text-white mt-1">{confirmReward.title}</p>
            <p className="text-xs text-muted-c mt-1">{confirmReward.description}</p>
            <div className="mt-4 flex items-center justify-between rounded-lg border border-[color:var(--border-subtle)] bg-black/30 px-3 py-2 text-sm">
              <span className="text-muted-c">Cost</span><span className="text-gold-soft font-semibold">{confirmReward.points_cost.toLocaleString()} pts</span>
            </div>
            <div className="mt-1.5 flex items-center justify-between px-3 text-xs text-muted-c"><span>Balance after</span><span>{(balance - confirmReward.points_cost).toLocaleString()} pts</span></div>
            <button onClick={doRedeem} disabled={redeeming} data-testid="redeem-confirm-btn" className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gold px-5 py-3 text-sm font-semibold text-black hover:bg-gold-soft disabled:opacity-60">
              {redeeming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />} Confirm &amp; claim
            </button>
            <p className="mt-2 text-center text-[11px] text-muted-c">You'll get a code to show Ascendia, or we'll apply it to your next service.</p>
          </div>
        </div>
      )}
    </div>
  );
}
